import { useEffect } from "react";
import { useAtomValue } from "jotai";

import { referenceLinesEnabledAtom } from "../atoms/hangingProtocolAtoms";
import { getCornerstone, getCornerstoneTools } from "../utils/dicomLoader";
import { SHARED_RENDERING_ENGINE_ID } from "../utils/renderingEngine";
import { resolveAnnotationPlane, type AnatomicalPlane } from "../utils/planeResolver";
import {
  REFERENCE_LINES_AXIAL,
  REFERENCE_LINES_CORONAL,
  REFERENCE_LINES_SAGITTAL,
} from "../tools/planeReferenceLineTools";

const TOOL_GROUP_ID = `${SHARED_RENDERING_ENGINE_ID}_toolGroup`;

const TOOL_FOR_PLANE: Record<AnatomicalPlane, string> = {
  axial: REFERENCE_LINES_AXIAL,
  coronal: REFERENCE_LINES_CORONAL,
  sagittal: REFERENCE_LINES_SAGITTAL,
};

const ALL_TOOLS = [REFERENCE_LINES_AXIAL, REFERENCE_LINES_CORONAL, REFERENCE_LINES_SAGITTAL];

// Wires Cornerstone3D's per-plane ReferenceLines tools to referenceLinesEnabledAtom.
// Each plane's source viewport is picked by classifying its camera.viewPlaneNormal,
// so this works regardless of which slot (top-left/top-right/etc) holds which plane.
// Subscribes to VOLUME_VIEWPORT_NEW_VOLUME so the sync re-runs once viewports have
// loaded their volumes (otherwise a default-enabled toggle shows nothing on first
// load: the effect runs before any camera has a viewPlaneNormal).
// Run once at the HangingProtocolGrid root.
export const useReferenceLinesSync = () => {
  const enabled = useAtomValue(referenceLinesEnabledAtom);

  useEffect(() => {
    let cs: ReturnType<typeof getCornerstone>;
    let csTools: ReturnType<typeof getCornerstoneTools>;
    try {
      cs = getCornerstone();
      csTools = getCornerstoneTools();
    } catch {
      return;
    }

    const apply = () => {
      const toolGroup = csTools.ToolGroupManager.getToolGroup(TOOL_GROUP_ID);
      const engine = cs.getRenderingEngine(SHARED_RENDERING_ENGINE_ID);
      if (!toolGroup) return;

      if (!enabled) {
        for (const toolName of ALL_TOOLS) {
          try {
            toolGroup.setToolDisabled(toolName);
          } catch {
            /* tool not registered yet */
          }
        }
        return;
      }

      if (!engine) return;

      // Classify each viewport by its camera's viewPlaneNormal, then pair each
      // plane with the matching tool. If multiple viewports share a plane (e.g.
      // classification layout has Axial Pre + Axial Post), the first wins.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const viewports = engine.getViewports() as any[];
      const sourceByPlane: Partial<Record<AnatomicalPlane, string>> = {};
      for (const vp of viewports) {
        const id = vp?.id;
        if (typeof id !== "string") continue;
        let camera;
        try {
          camera = vp.getCamera?.();
        } catch {
          continue;
        }
        if (!camera?.viewPlaneNormal) continue;
        const plane = resolveAnnotationPlane({
          metadata: { viewPlaneNormal: camera.viewPlaneNormal },
        });
        if (!plane) continue;
        if (!sourceByPlane[plane]) sourceByPlane[plane] = id;
      }

      for (const plane of ["axial", "coronal", "sagittal"] as AnatomicalPlane[]) {
        const toolName = TOOL_FOR_PLANE[plane];
        const sourceId = sourceByPlane[plane];
        if (!sourceId) {
          // No viewport for this plane (yet, or never in this layout) — keep
          // the tool disabled so it doesn't draw with a stale source.
          try {
            toolGroup.setToolDisabled(toolName);
          } catch {
            /* not registered */
          }
          continue;
        }
        try {
          toolGroup.setToolConfiguration(toolName, { sourceViewportId: sourceId });
          toolGroup.setToolEnabled(toolName);
        } catch {
          /* tool may not be registered on the group yet */
        }
      }
    };

    // First attempt at mount. Likely a no-op or partial because this hook
    // mounts in parallel with VolumeViewport's async setup.
    apply();

    if (!enabled) return;

    // Race window: VolumeViewport asynchronously creates the tool group, adds
    // viewports, and calls setVolumes — all after our mount. Cornerstone's
    // ELEMENT_ENABLED / VOLUME_VIEWPORT_NEW_VOLUME events don't always reach
    // this listener in time (some fire on the per-element target, not the
    // global one). A short polling retry is the simplest robust trigger;
    // apply() is idempotent so over-firing is fine. Stops once all 3 planes
    // are paired or after the budget runs out.
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      apply();
      const engine = cs.getRenderingEngine(SHARED_RENDERING_ENGINE_ID);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const vps = (engine?.getViewports() ?? []) as any[];
      const planesFound = new Set<AnatomicalPlane>();
      for (const vp of vps) {
        const n = vp?.getCamera?.()?.viewPlaneNormal;
        if (!n) continue;
        const p = resolveAnnotationPlane({ metadata: { viewPlaneNormal: n } });
        if (p) planesFound.add(p);
      }
      if (planesFound.size === 3 || attempts >= 20) {
        window.clearInterval(interval);
      }
    }, 250);

    return () => window.clearInterval(interval);
  }, [enabled]);
};
